import { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha, useTheme, type Theme } from "@mui/material/styles";
import { useStockLocations } from "../../api/stocklocations";
import { useStockRelocation } from "../../api/stockrelocation";
import { ProductStock, StockLocation } from "../../shared/types";
import { stockLocationDisplayName } from "../../shared/stock-location-display-name";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

type Slot = { level: number; column: number };

interface RelocationDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    stockItem: ProductStock;
}

type MoveMode = "into_stock" | "relocate";

export default function RelocationDialog({ open, onClose, onSuccess, stockItem }: RelocationDialogProps) {
    const theme = useTheme();
    const { stockLocations, loading: locationsLoading } = useStockLocations();
    const { requestIntoStock, requestRelocation, submitting, error } = useStockRelocation();

    const [mode, setMode] = useState<MoveMode>("into_stock");
    const [splitPallet, setSplitPallet] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<StockLocation | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

    useEffect(() => {
        if (!open) {
            setMode("into_stock");
            setSplitPallet(false);
            setSelectedLocation(null);
            setSelectedSlot(null);
        }
    }, [open]);

    useEffect(() => {
        setSelectedSlot(null);
    }, [selectedLocation]);

    const canSubmit =
        mode === "into_stock"
            ? !submitting
            : selectedLocation !== null && selectedSlot !== null && !submitting;

    async function handleSubmit() {
        if (mode === "into_stock") {
            const success = await requestIntoStock(stockItem.id);
            if (success) {
                onSuccess();
                onClose();
            }
        } else {
            if (!selectedLocation || !selectedSlot) return;

            const success = await requestRelocation(
                stockItem.id,
                selectedLocation.id,
                selectedSlot.level,
                (selectedLocation.columns ?? 1) > 1 ? selectedSlot.column : undefined,
                splitPallet
            );

            if (success) {
                onSuccess();
                onClose();
            }
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Request Stock Move</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Moving <strong>{stockItem.product.code}</strong> from{" "}
                            <strong>{stockItem.location.name} ({stockLocationDisplayName(stockItem.location, stockItem.level, stockItem.column)})</strong>
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error">{error}</Alert>
                    )}

                    <RadioGroup
                        row
                        value={mode}
                        onChange={(_, v) => setMode(v as MoveMode)}
                    >
                        <FormControlLabel value="into_stock" control={<Radio />} label="Into stock" />
                        <FormControlLabel value="relocate" control={<Radio />} label="Relocate" />
                    </RadioGroup>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={splitPallet}
                                    onChange={(_, checked) => setSplitPallet(checked)}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">Split pallet</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Split the pallet when moving this stock item.
                                    </Typography>
                                </Box>
                            }
                        />
                    </FormGroup>

                    {mode === "relocate" && (
                        <>
                            <Autocomplete
                                options={stockLocations}
                                loading={locationsLoading}
                                value={selectedLocation}
                                onChange={(_, value) => setSelectedLocation(value)}
                                getOptionLabel={(option) => `${option.name} (${option.warehouse.name})`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                filterOptions={(options, { inputValue }) => {
                                    const q = inputValue.toLowerCase();
                                    return options.filter(
                                        (o) =>
                                            o.name.toLowerCase().includes(q) ||
                                            o.warehouse.name.toLowerCase().includes(q)
                                    );
                                }}
                                sx={{
                                    "& .MuiIconButton-root": {
                                        border: "none",
                                        background: "none",
                                        backgroundColor: "transparent",
                                        "&:hover": {
                                            background: "none",
                                            backgroundColor: "transparent",
                                        },
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search locations..."
                                        hiddenLabel
                                        label=""
                                    />
                                )}
                            />

                            {selectedLocation && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                                        Select a position
                                    </Typography>
                                    {(selectedLocation.columns ?? 1) > 1 ? (
                                        <_LevelColumnGrid
                                            location={selectedLocation}
                                            selectedSlot={selectedSlot}
                                            onSelect={setSelectedSlot}
                                            theme={theme}
                                        />
                                    ) : (
                                        <_LevelStack
                                            location={selectedLocation}
                                            selectedLevel={selectedSlot?.level ?? null}
                                            onSelect={(level) => setSelectedSlot({ level, column: 1 })}
                                            theme={theme}
                                        />
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {submitting ? "Submitting..." : "Request Move"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function _SlotCell({
    location,
    level,
    column,
    isSelected,
    onClick,
    theme,
}: {
    location: StockLocation;
    level: number;
    column: number;
    isSelected: boolean;
    onClick: () => void;
    theme: Theme;
}) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 44,
                minHeight: 40,
                border: "1px solid",
                borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
                backgroundColor: isSelected ? theme.palette.primary.main : "transparent",
                color: isSelected ? "#fff" : theme.palette.text.secondary,
                cursor: "pointer",
                transition: "all 120ms ease",
                "&:hover": isSelected
                    ? {}
                    : {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                        color: theme.palette.primary.main,
                    },
            }}
        >
            <Typography variant="caption" fontWeight={isSelected ? 700 : 500}>
                {stockLocationDisplayName(location, level, column)}
            </Typography>
        </Box>
    );
}

function _LevelColumnGrid({
    location,
    selectedSlot,
    onSelect,
    theme,
}: {
    location: StockLocation;
    selectedSlot: Slot | null;
    onSelect: (slot: Slot) => void;
    theme: Theme;
}) {
    const levelList = Array.from({ length: location.levels }, (_, i) => location.levels - i);
    const columnList = Array.from({ length: location.columns }, (_, i) => i + 1);

    return (
        <Box sx={{ maxHeight: 280, overflow: "auto" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${location.columns}, minmax(44px, 1fr))`,
                    gap: 0.5,
                }}
            >
                {levelList.flatMap((level) =>
                    columnList.map((column) => (
                        <_SlotCell
                            key={`${level}-${column}`}
                            location={location}
                            level={level}
                            column={column}
                            isSelected={
                                selectedSlot?.level === level && selectedSlot?.column === column
                            }
                            onClick={() => onSelect({ level, column })}
                            theme={theme}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
}

function _LevelStack({
    location,
    selectedLevel,
    onSelect,
    theme,
}: {
    location: StockLocation;
    selectedLevel: number | null;
    onSelect: (level: number) => void;
    theme: Theme;
}) {
    return (
        <Stack spacing={0}>
            {Array.from({ length: location.levels }, (_, i) => location.levels - i).map((level) => {
                const isSelected = selectedLevel === level;
                return (
                    <Box
                        key={level}
                        onClick={() => onSelect(level)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 44,
                            border: "1px solid",
                            borderColor: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.divider,
                            borderBottom: level === 1 ? "1px solid" : "none",
                            borderBottomColor: isSelected
                                ? theme.palette.primary.main
                                : theme.palette.divider,
                            "&:first-of-type": {
                                borderTopLeftRadius: theme.shape.borderRadius,
                                borderTopRightRadius: theme.shape.borderRadius,
                            },
                            "&:last-of-type": {
                                borderBottom: "1px solid",
                                borderBottomColor: isSelected
                                    ? theme.palette.primary.main
                                    : theme.palette.divider,
                                borderBottomLeftRadius: theme.shape.borderRadius,
                                borderBottomRightRadius: theme.shape.borderRadius,
                            },
                            backgroundColor: isSelected
                                ? theme.palette.primary.main
                                : "transparent",
                            color: isSelected ? "#fff" : theme.palette.text.secondary,
                            cursor: "pointer",
                            transition: "all 120ms ease",
                            "&:hover": isSelected
                                ? {}
                                : {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    borderColor: alpha(theme.palette.primary.main, 0.4),
                                    color: theme.palette.primary.main,
                                },
                        }}
                    >
                        <Typography variant="body2" fontWeight={isSelected ? 700 : 500}>
                            {stockLocationDisplayName(location, level, 1)}
                        </Typography>
                    </Box>
                );
            })}
        </Stack>
    );
}
